import React, { useEffect } from "react";

import { useMedicalPlansQuery, useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import { ApplicationLayout } from "EmployerApplication/Components";
import { CompanyInformationPage } from "EmployerApplication/pages";
import CoverageOptionsPage from "EmployerApplication/pages/CoverageOptionsPage";
import DonePage from "EmployerApplication/pages/DonePage";
import EmployeeStatusPage from "EmployerApplication/pages/EmployeeStatusPage";
import OpenEnrollmentPage from "EmployerApplication/pages/OpenEnrollmentPage";
import PaymentInformation from "EmployerApplication/pages/PaymentInformation";
import DocumentUploadPage from "EmployerApplication/pages/RenewalDocumentUploadPage";
import SubmittedPage from "EmployerApplication/pages/SubmittedPage";
import { getFirstIncompleteURL, getCompanyPolicy } from "EmployerApplication/Selectors";
import { requestDentalPlans, requestVisionPlans, requestLifePlans } from "QuotePage/Actions";
import { history } from "QuotePage/Store";
import { useSelector, useDispatch } from "react-redux";
import { Switch, Route, withRouter } from "react-router-dom";
import { BasicLoading, GenericErrorPage } from "ZeroRuby";

import { withErrorHandler } from "Cobalt/ErrorHandler";
import { appendAuthParams } from "Common/TokenAuth/Utils";
import { nestedPrefixRoutes } from "EmployerApplication/urls";

const useLoadDataRenewal = (companyIdToken?: string | null) => {
  const { isLoading: isLoadingMedicalPlans } = useMedicalPlansQuery();
  const companyPolicy = useSelector(getCompanyPolicy);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: "REQUEST_ER_APP_INITIAL_LOAD" });
  }, [dispatch]);

  useEffect(() => {
    if (companyIdToken) {
      dispatch(requestDentalPlans({ companyIdToken }));
      dispatch(requestVisionPlans({ companyIdToken }));
      dispatch(requestLifePlans({ companyIdToken }));
    }
  }, [companyIdToken, dispatch]);

  return {
    isLoading: isLoadingMedicalPlans || !companyPolicy,
  };
};



const EmployerRenewalApplication = () => {
  const { data: companyApplication, isLoading: isAppLoading } = useGetCurrentCompanyApplicationQuery();
  const dispatch = useDispatch();
  const firstIncomplete = useSelector(getFirstIncompleteURL);

  const companyIdToken = companyApplication?.companyIdToken || null;
  const { isLoading: isBootLoading } = useLoadDataRenewal(companyIdToken);

  useEffect(() => {
    if (companyApplication) {
      dispatch({ type: "RECEIVE_COMPANY_POLICY_ID", payload: companyApplication.id });
    }
  }, [companyApplication, dispatch]);

  useEffect(() => {
    const path = history.location.pathname;
    if (/^\/employer-renewal\/?$/.test(path) && companyApplication && !isBootLoading) {
      history.push(appendAuthParams(firstIncomplete));
    }
  }, [firstIncomplete, companyApplication, isBootLoading]);

  if (isBootLoading || isAppLoading || !companyApplication) {
    return <BasicLoading />;
  }

  const routes = [
    { path: "/employer-renewal/coverage-options", Component: CoverageOptionsPage, withQuotePanel: false },
    { path: "/employer-renewal/company-information", Component: CompanyInformationPage, withQuotePanel: true },
    { path: "/employer-renewal/document-upload", Component: DocumentUploadPage, withQuotePanel: true },
    { path: "/employer-renewal/open-enrollment", Component: OpenEnrollmentPage, withQuotePanel: true },
    { path: "/employer-renewal/employee-status", Component: EmployeeStatusPage, withQuotePanel: true },
    { path: "/employer-renewal/payment-information", Component: PaymentInformation, withQuotePanel: true },
    { path: "/employer-renewal/done", Component: DonePage, withQuotePanel: true },
  ] as const;

  return companyApplication.submittedAt !== null ? (
    <SubmittedPage />
  ) : (
    <Switch>
      {routes.map(({ path, Component, withQuotePanel }) => (
        <Route exact path={path} key={path}>
          <ApplicationLayout
            withQuotePanel={withQuotePanel}
            isRenewal={true}
          >
            <Component />
          </ApplicationLayout>
        </Route>
      ))}
    </Switch>
  );
};

const EmployerRenewalApplicationWithErrorHandler = withErrorHandler(EmployerRenewalApplication, {
  component: GenericErrorPage,
  cta: "Back to Dashboard",
  href: "/employer-dashboard",
});

export default withRouter(EmployerRenewalApplicationWithErrorHandler);
